// Study content (texts + images) — HANDOFF schema Sections 6 & 12.
// Fetched once from the Worker, cached into IndexedDB content_cache,
// and refreshed if the cache is more than 24h old (Dashboard behavior,
// Section 10).

import { fetchContentManifest, fetchJson } from '../worker/api';
import { getContentCache, putContentCache, getAllContentCache, clearContentCache } from '../db/indexedDB';

const MANIFEST_CACHE_KEY = '__content_manifest__';
const REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000;

export async function ensureContentCached() {
  const cachedManifest = await getContentCache(MANIFEST_CACHE_KEY);
  const stale = !cachedManifest || Date.now() - new Date(cachedManifest.cached_at).getTime() > REFRESH_INTERVAL_MS;

  if (!stale) return JSON.parse(cachedManifest.data);

  try {
    const manifest = await fetchContentManifest();
    await putContentCache(MANIFEST_CACHE_KEY, 'manifest', JSON.stringify(manifest));

    // Prefetch text passage JSON bodies (small) so TEXT_RECORDING works offline.
    // Images are left to the service worker's CacheFirst 'study-content-v1'
    // route (Section 15) since fetching all 35 up front would be heavy on
    // first load — they're cached lazily the first time each is displayed.
    await Promise.all(
      manifest.texts.map(async (t) => {
        const existing = await getContentCache(t.key);
        if (existing) return;
        try {
          const passage = await fetchJson(resolveContentUrl(t.url));
          await putContentCache(t.key, 'text', JSON.stringify(passage));
        } catch {
          // Offline on first load with no prior cache — TextRecordingScreen
          // will surface this as "content unavailable, check connection".
        }
      })
    );

    return manifest;
  } catch (err) {
    if (cachedManifest) return JSON.parse(cachedManifest.data);
    throw err;
  }
}

export function resolveContentUrl(relativeUrl) {
  const base = import.meta.env.VITE_R2_PUBLIC_URL?.replace(/\/$/, '') ?? '';
  return `${base}/${relativeUrl.replace(/^\//, '')}`;
}

export async function getPassage(key) {
  const cached = await getContentCache(key);
  if (cached) return JSON.parse(cached.data);
  return null;
}

export async function getManifest() {
  const cached = await getContentCache(MANIFEST_CACHE_KEY);
  return cached ? JSON.parse(cached.data) : null;
}

export async function refreshAllContentCache() {
  await clearContentCache();
  return ensureContentCached();
}

export async function listCachedKeys() {
  const all = await getAllContentCache();
  return all.map((r) => r.key);
}
